import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  LayoutDashboard, Clock, CheckSquare, Users, FileText,
  LogOut, Menu, X, ChevronRight, FolderKanban, ScrollText, UserCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const NAV_ITEMS = {
  volunteer: [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/my-hours', label: 'My Hours', icon: FileText },
    { path: '/profile', label: 'My Profile', icon: UserCircle },
  ],
  supervisor: [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/approve-hours', label: 'Approve Hours', icon: CheckSquare },
    { path: '/submit-hours', label: 'Submit Hours', icon: Clock },
    { path: '/my-hours', label: 'All Hours', icon: FileText },
    { path: '/reports', label: 'Reports', icon: FileText },
    { path: '/profile', label: 'My Profile', icon: UserCircle },
  ],
  admin: [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/approve-hours', label: 'Approve Hours', icon: CheckSquare },
    { path: '/manage-users', label: 'Manage Users', icon: Users },
    { path: '/committees', label: 'Committees', icon: FolderKanban },
    { path: '/audit-log', label: 'Audit Log', icon: ScrollText },
    { path: '/submit-hours', label: 'Submit Hours', icon: Clock },
    { path: '/my-hours', label: 'All Hours', icon: FileText },
    { path: '/reports', label: 'Reports', icon: FileText },
    { path: '/profile', label: 'My Profile', icon: UserCircle },
  ],
};

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const items = NAV_ITEMS[user?.role] || NAV_ITEMS.volunteer;

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="flex min-h-screen bg-[#FAF8F5]" data-testid="app-layout">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/30 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`fixed lg:sticky top-0 left-0 z-50 h-screen w-64 bg-[#FAF8F5] border-r border-[#E8E3DD] flex flex-col transition-transform duration-200 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`} data-testid="sidebar">
        <div className="p-6 border-b border-[#E8E3DD] flex items-center gap-3">
          <img src="/svdp-logo.png" alt="SVdP" className="w-10 h-10 flex-shrink-0" />
          <div>
            <h1 className="font-['Manrope'] text-base font-bold text-[#2D2B2A] leading-tight">Community Service</h1>
            <p className="text-xs text-[#5C5855]">Tracker</p>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {items.map((item) => {
            const isActive = location.pathname === item.path;
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.path.replace('/', '')}`}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors duration-150 ${
                  isActive
                    ? 'bg-[#D3735A]/10 text-[#D3735A] border border-[#D3735A]/20'
                    : 'text-[#5C5855] hover:bg-[#F2EFEB] hover:text-[#2D2B2A]'
                }`}
              >
                <Icon className="h-4 w-4 flex-shrink-0" />
                {item.label}
                {isActive && <ChevronRight className="h-3 w-3 ml-auto" />}
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-[#E8E3DD]">
          <div className="px-4 py-2 mb-3">
            <p className="text-sm font-semibold text-[#2D2B2A] truncate" data-testid="user-name">{user?.name}</p>
            <p className="text-xs text-[#5C5855] capitalize" data-testid="user-role">{user?.role}</p>
          </div>
          <Button
            data-testid="logout-button"
            onClick={handleLogout}
            variant="outline"
            className="w-full border-[#D1C9C0] text-[#5C5855] hover:bg-[#F2EFEB] hover:text-[#B74134] rounded-lg"
          >
            <LogOut className="h-4 w-4 mr-2" /> Sign Out
          </Button>
          <p className="text-[10px] text-[#8A847F] text-center mt-3 leading-tight">Powered by SVdP<br/>Mary, Mother of God Outreach Ministries</p>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Top bar (mobile) */}
        <header className="lg:hidden bg-white/90 backdrop-blur-xl border-b border-[#E8E3DD] sticky top-0 z-30 px-4 py-3 flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-menu-button"
          >
            <Menu className="h-5 w-5" />
          </Button>
          <h1 className="font-['Manrope'] text-sm font-bold text-[#2D2B2A]">Community Service Tracker</h1>
          <div className="w-8" />
        </header>

        <main className="flex-1 p-6 md:p-8 lg:p-10 max-w-7xl mx-auto w-full">
          {children}
        </main>
      </div>
    </div>
  );
}
